import { API_BASE_URL as API_BASE } from '../config/api';
import { createAuthenticatedApiClient } from './authenticatedApiClient';
import { clearRequestCacheByPrefix, withRequestCache } from './requestCache';

const apiClient = createAuthenticatedApiClient({
  baseURL: `${API_BASE}/api/v1/gdelt`,
});

const CACHE_KEY_ALL_COUNTRY_SENTIMENTS = 'gdelt:all-country-sentiments';

/**
 * GDELT Service - Provides access to country sentiment data from GDELT API
 */
export const gdeltService = {
  /**
   * Get list of all supported countries
   * @returns {Promise} List of countries
   */
  async getSupportedCountries() {
    try {
      const response = await apiClient.get('/countries');
      return {
        success: true,
        data: response.data.data?.countries || [],
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || error.message || 'Failed to fetch supported countries'
      };
    }
  },

  /**
   * Get sentiment data for all countries
   * @returns {Promise} Array of country sentiment data
   */
  async getAllCountriesSentiment() {
    try {
      const response = await withRequestCache({
        key: CACHE_KEY_ALL_COUNTRY_SENTIMENTS,
        ttlMs: 5 * 60 * 1000,
        requestFn: () => apiClient.get('/sentiments'),
      });
      return {
        success: true,
        data: response.data.data || [],
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || error.message || 'Failed to fetch sentiments'
      };
    }
  },

  /**
   * Get sentiment data for a specific country
   * @param {string} country - Country name
   * @returns {Promise} Country sentiment data
   */
  async getCountrySentiment(country) {
    try {
      const response = await apiClient.get(`/sentiments/${country}`);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || error.message || `Failed to fetch sentiment for ${country}`
      };
    }
  },

  /**
   * Get historical sentiment data for a country
   * @param {string} country - Country name
   * @param {number} limit - Maximum number of records (default: 100)
   * @returns {Promise} Historical sentiment data
   */
  async getCountryHistory(country, limit = 100) {
    try {
      const response = await apiClient.get(`/history/${country}`, {
        params: { limit }
      });
      return {
        success: true,
        data: response.data.data || [],
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || error.message || `Failed to fetch history for ${country}`
      };
    }
  },

  /**
   * Get sentiment trend for a country
   * @param {string} country - Country name
   * @param {number} days - Number of days to analyze (default: 30)
   * @returns {Promise} Trend analysis data
   */
  async getCountryTrend(country, days = 30) {
    try {
      const response = await apiClient.get(`/trend/${country}`, {
        params: { days }
      });
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || error.message || `Failed to fetch trend for ${country}`
      };
    }
  },

  /**
   * Compare sentiment across multiple countries
   * @param {Array<string>} countries - List of country names
   * @param {number} days - Comparison period in days (default: 30)
   * @returns {Promise} Comparison data
   */
  async compareCountries(countries, days = 30) {
    try {
      const response = await apiClient.post('/compare', {
        countries,
        days
      });
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || error.message || 'Failed to compare countries'
      };
    }
  },

  /**
   * Get summary statistics for all countries
   * @returns {Promise} Summary statistics
   */
  async getSentimentSummary() {
    try {
      const response = await apiClient.get('/summary');
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || error.message || 'Failed to fetch sentiment summary'
      };
    }
  },

  /**
   * Check if a country is supported
   * @param {string} country - Country name
   * @returns {Promise} Support status
   */
  async isCountrySupported(country) {
    try {
      const response = await apiClient.get(`/countries/${country}/supported`);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        data: { supported: false },
        message: error.response?.data?.message || error.message || `Failed to check support for ${country}`
      };
    }
  },

  /**
   * Manually refresh GDELT data
   * @returns {Promise} Refresh result
   */
  async refreshData() {
    try {
      const response = await apiClient.post('/refresh');
      clearRequestCacheByPrefix('gdelt:');
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || error.message || 'Failed to refresh data'
      };
    }
  },

  /**
   * Get categorized news for a country
   * @param {string} country - Country name
   * @returns {Promise} Categorized news article IDs
   */
  async getCategorizedNews(country) {
    try {
      const response = await apiClient.get(`/news/${country}`);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || error.message || `Failed to fetch news for ${country}`
      };
    }
  },

  /**
   * Get positive news articles for a country
   * @param {string} country - Country name
   * @returns {Promise} Positive news articles
   */
  async getPositiveNews(country) {
    try {
      const response = await apiClient.get(`/news/${country}/positive`);
      return {
        success: true,
        data: response.data.data || [],
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || error.message || `Failed to fetch positive news for ${country}`
      };
    }
  },

  /**
   * Get negative news articles for a country
   * @param {string} country - Country name
   * @returns {Promise} Negative news articles
   */
  async getNegativeNews(country) {
    try {
      const response = await apiClient.get(`/news/${country}/negative`);
      return {
        success: true,
        data: response.data.data || [],
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || error.message || `Failed to fetch negative news for ${country}`
      };
    }
  },

  /**
   * Get neutral news articles for a country
   * @param {string} country - Country name
   * @returns {Promise} Neutral news articles
   */
  async getNeutralNews(country) {
    try {
      const response = await apiClient.get(`/news/${country}/neutral`);
      return {
        success: true,
        data: response.data.data || [],
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || error.message || `Failed to fetch neutral news for ${country}`
      };
    }
  }
};

